import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { restoreUserSession, userSession } from './userSessionStorageManager';
import { CartDiscounts, CartItem, useGetApprovedAmount } from './flowManager';
import { atomWithReset, useResetAtom } from 'jotai/utils';
import { appDataByMerchant } from './configManager';
import { CartDetails } from '@chargeafter/payment-sdk';
import { getFlags } from '@chargeafter/feature-flag';
import { cartTotalErrorMessage } from '../constants';
import { formatCurrency } from '../formattingUtils';
import { useGetCheckoutConfiguration } from './lendersConfigurationManager';

restoreUserSession();

const AdvancedCartDetailsAtom = atomWithReset<CartDetails>(
  userSession?.advancedCart || {
    items: [],
    shippingAmount: 0,
    taxAmount: 0,
    totalAmount: 0,
  },
);
const selectedCartToggleState = atom<boolean>(
  !!userSession?.switchToAdvancedCart,
);

const cartTypeToggleAtom = atom(
  async (get) => {
    const appData = await get(appDataByMerchant);
    const cartType = appData?.configuration.settings?.cartType;
    const selectedCartType = get(selectedCartToggleState);

    switch (cartType) {
      case 'Advanced':
        return true;
      case 'BasicAndAdvanced':
        return selectedCartType;
      default:
        return false;
    }
  },
  (_, set, value: boolean) => {
    set(selectedCartToggleState, value);
  },
);

export const useGetCartTypeToggle = () => useAtomValue(cartTypeToggleAtom);
export const useCartTypeToggle = () => useAtom(cartTypeToggleAtom);

const MerchantOrderId = atom<string | undefined>(userSession?.merchantOrderId);

const SingleItemName = atom<string>(userSession?.itemName || '');

const SelectedItem = atom<CartItem | undefined>(undefined);

const SingleItemCartState = atom(
  userSession?.cartItems || new Array<CartItem>(0),
);

export const useGetMerchantOrderId = () => useAtomValue(MerchantOrderId);
export const useSetManualMerchantOrderId = () => useSetAtom(MerchantOrderId);

export const useGetSingleItemName = () => useAtomValue(SingleItemName);
export const useSetSingleItemName = () => useSetAtom(SingleItemName);

export const useCartSingleItem = () => useAtom(SingleItemCartState);
export const useSetSingleItemCart = () => useSetAtom(SingleItemCartState);
export const useGetSingleItemCart = () => {
  const cartItems = useAtomValue(SingleItemCartState);
  const cart: CartDetails = {
    items: cartItems,
    shippingAmount: 0,
    taxAmount: 0,
    totalAmount: 0,
  };
  return cart;
};

export const useEditedItem = () => useAtom(SelectedItem);
export const useGetEditedItem = () => useAtomValue(SelectedItem);
export const useSetSelectedItem = () => useSetAtom(SelectedItem);

export const useAdvancedCartDetails = () => useAtom(AdvancedCartDetailsAtom);
export const useGetAdvancedCartDetails = () =>
  useAtomValue(AdvancedCartDetailsAtom);
export const useResetAdvancedCartDetails = () =>
  useResetAtom(AdvancedCartDetailsAtom);

export const useIsCartOverBooked = () =>
  useGetCart().totalAmount > useGetApprovedAmount();

export const calcCart = (cart: CartDetails) => {
  const { items, shippingAmount, taxAmount, discounts } = cart;
  const discountAmount = discounts
    ? discounts.reduce((acc, cur) => acc + +cur.amount, 0)
    : 0;
  const itemsTotal = items
    .map((i) => calcItem(i))
    .reduce((acc, cur) => acc + cur, 0);
  return itemsTotal + +shippingAmount + +taxAmount - +discountAmount;
};

export const calcItem = (item: CartItem) =>
  +item.price * +item.quantity + Number(item.warranty?.price ?? 0);

export const useGetCart = (): CartDetails => {
  const singleItemCart = useGetSingleItemCart();
  const advancedCart = useGetAdvancedCartDetails();
  const isAdvancedCart = useGetCartTypeToggle();
  const merchantOrderId = useGetMerchantOrderId();

  return {
    items: isAdvancedCart ? advancedCart.items : singleItemCart.items,
    totalAmount: isAdvancedCart
      ? calcCart(advancedCart)
      : calcCart(singleItemCart),
    taxAmount: isAdvancedCart ? advancedCart.taxAmount || 0 : 0,
    shippingAmount: isAdvancedCart ? advancedCart.shippingAmount || 0 : 0,

    ...(isAdvancedCart &&
      advancedCart.discounts && { discounts: advancedCart.discounts }),
    merchantOrderId: isAdvancedCart
      ? advancedCart.merchantOrderId
      : merchantOrderId,
  };
};

export const getValueFromCartDetails = (
  value?: string | number | CartDiscounts,
) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value[0] : '';
  }
  return value || '';
};

export const useSingleLenderCartAmountError = () => {
  const checkoutConfiguration = useGetCheckoutConfiguration();
  const singleItemCart = useGetSingleItemCart();
  const advancedCart = useGetAdvancedCartDetails();
  const isAdvancedCart = useGetCartTypeToggle();
  const { ffSalesAppShouldUseCartLimitation } = getFlags();

  if (!ffSalesAppShouldUseCartLimitation) {
    return '';
  }

  const singleLenderMaxAmount =
    checkoutConfiguration?.lendersConfigurations.length === 1 &&
    checkoutConfiguration?.lendersConfigurations[0].maximalCartValue
      ? checkoutConfiguration?.lendersConfigurations[0].maximalCartValue
      : 0;

  if (!singleLenderMaxAmount) {
    return '';
  }

  const amountCartError =
    singleLenderMaxAmount <
    (isAdvancedCart ? calcCart(advancedCart) : calcCart(singleItemCart));

  const errorMessage = amountCartError
    ? `${cartTotalErrorMessage.maxAmount} ${formatCurrency(
        singleLenderMaxAmount,
      )}`
    : '';

  return errorMessage;
};
